/**
 * ClueLozenge Component
 *
 * A rounded rectangle that displays revealed clue text.
 * Stacks above/below HintButton in ClueCell.
 */

import React, { useMemo } from 'react';
import { fitClueText } from '../../utils/textFitting';
import type { DisplayHints } from '../../types';

interface ClueLozengeProps {
  /** The clue text to display (e.g., "BITE") */
  text: string;
  /** Row index for color alternation (0-5) */
  rowIndex: number;
  /** Whether to show green guess target background */
  showGreenGuessBackground?: boolean;
  /** SVG width in pixels */
  width?: number;
  /** SVG height in pixels */
  height?: number;
  /** Font size in pixels (used as fallback when autoFit is false) */
  fontSize?: number;
  /** Additional inline styles */
  style?: React.CSSProperties;
  /** Additional CSS class */
  className?: string;
  /** Use rounded 196×66 rect (matching HintButton shape) instead of sharp 208×78 background */
  rounded?: boolean;
  /** Override the text fill color (e.g., gold during celebration) */
  textColorOverride?: string;
  /** Override the background fill color (e.g., gold for active guess cell) */
  bgColorOverride?: string;
  /** Display hints for manual line breaks/hyphens */
  displayHint?: DisplayHints;
  /** Enable auto text fitting (default true) */
  autoFit?: boolean;
}

const DEFAULT_WIDTH = 196;
const DEFAULT_HEIGHT = 66;
const DEFAULT_FONT_SIZE = 28;

const ClueLozenge: React.FC<ClueLozengeProps> = ({
  text,
  rowIndex,
  showGreenGuessBackground = false,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  fontSize = DEFAULT_FONT_SIZE,
  style = {},
  className = '',
  rounded = false,
  textColorOverride,
  bgColorOverride,
  displayHint,
  autoFit = true,
}) => {
  // Alternating colors: even rows = light bg/dark text, odd rows = dark bg/light text
  const isEvenRow = rowIndex % 2 === 0;
  const bgColor = bgColorOverride || (isEvenRow ? '#f7e5c5' : '#262626');

  // Text color: override if provided, cream for green guess backgrounds, otherwise alternating
  const textColor = textColorOverride || (showGreenGuessBackground ? '#f7e5c5' : (isEvenRow ? '#262626' : '#f7e5c5'));

  // Text fitting: auto-determine font size and line breaks
  const fit = useMemo(
    () => autoFit ? fitClueText(text, width, displayHint) : null,
    [text, width, displayHint, autoFit]
  );
  const effectiveFontSize = fit ? fit.fontSize : fontSize;
  const lines = fit ? fit.lines : [text.toUpperCase()];

  // Rounded mode: 196×66 with rx=12 (matches HintButton shape, used during animation)
  // Default mode: 208×78 sharp rect centered in viewBox (seamless grid background)
  const rectWidth = rounded ? width : 208;
  const rectHeight = rounded ? height : 78;
  const rectX = rounded ? 0 : (width - rectWidth) / 2; // 0 or -6
  const rectY = rounded ? 0 : (height - rectHeight) / 2; // 0 or -6
  const cornerRadius = rounded ? 12 : 0;

  // Line spacing for multi-line text
  const lineHeight = effectiveFontSize * 1.2;
  const totalTextHeight = lines.length * lineHeight;
  const startY = (height - totalTextHeight) / 2 + lineHeight / 2;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ ...style, overflow: 'visible' }}
      className={className}
    >
      {/* Rectangle background */}
      <rect
        x={rectX}
        y={rectY}
        width={rectWidth}
        height={rectHeight}
        rx={cornerRadius}
        fill={bgColor}
      />

      {/* Green guess target background (when applicable) */}
      {showGreenGuessBackground && (
        <rect
          x="0"
          y="0"
          width="196"
          height="66"
          rx="12"
          fill="#387c47"
        />
      )}

      {/* Centered text — single or multi-line */}
      {lines.length === 1 && lines[0] && (
        <text
          x="50%"
          y="50%"
          dy=".1em"
          fill={textColor}
          fontFamily="Inter, sans-serif"
          fontSize={effectiveFontSize}
          fontWeight="700"
          textAnchor="middle"
          dominantBaseline="middle"
          pointerEvents="none"
        >
          {lines[0]}
        </text>
      )}
      {lines.length > 1 && (
        <text
          fill={textColor}
          fontFamily="Inter, sans-serif"
          fontSize={effectiveFontSize}
          fontWeight="700"
          textAnchor="middle"
          pointerEvents="none"
        >
          {lines.map((line, i) => (
            <tspan
              key={i}
              x="50%"
              y={startY + i * lineHeight}
              dominantBaseline="middle"
            >
              {line}
            </tspan>
          ))}
        </text>
      )}
    </svg>
  );
};

export default React.memo(ClueLozenge);
